import { Fragment, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, type TeamUser, type TechnicianRef, type SupervisorRef, type BranchRef } from '../lib/api'
import type { Role } from '../../shared/types'
import { useAuth } from '../lib/auth'
import { ImportWizard } from '../components/ImportWizard'
import { EmailLink } from '../components/links'
import { AccountCard } from '../components/AccountCard'

export function SettingsPage() {
  const { user, company } = useAuth()
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin'
  const formUrl = company ? `${window.location.origin}/f/${company.form_token}` : ''

  return (
    <div>
      <div className="page-title">Settings</div>
      <p className="page-sub">Manage your team, share your damage-report form, and import data.</p>

      <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
        {/* Share form link */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Public damage-report form</div>
          <p className="page-sub" style={{ marginBottom: 10 }}>
            Share this link with your field technicians — no login required.
          </p>
          <ShareLink url={formUrl} />
        </div>

        {isAdmin ? (
          <>
            <BranchSection />
            <SupervisorSection />
            <TechnicianSection formUrlBase={company ? `${window.location.origin}/f/${company.form_token}` : ''} />
            <TeamSection />
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Import existing damage log</div>
              <p className="page-sub" style={{ marginBottom: 12 }}>Bulk-import historical cases from a spreadsheet.</p>
              <ImportWizard />
            </div>
          </>
        ) : (
          <div className="card" style={{ color: 'var(--gray)', fontSize: 14 }}>
            Team management and data import are available to company administrators.
          </div>
        )}

        <AccountCard />
      </div>
    </div>
  )
}

function ShareLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input readOnly value={url} style={{ ...inputStyle, flex: 1 }} onFocus={(e) => e.target.select()} />
      <button
        className="btn btn-primary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          } catch {
            /* clipboard blocked; user can select manually */
          }
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  )
}

function BranchSection() {
  const [branches, setBranches] = useState<BranchRef[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')

  async function refresh() {
    setLoading(true)
    try {
      setBranches((await api.listBranches()).branches)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void refresh()
  }, [])

  async function add(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    try {
      await api.createBranch(name.trim())
      setName('')
      await refresh()
    } finally {
      setBusy(false)
    }
  }
  async function saveName(id: string) {
    if (editName.trim()) await api.updateBranch(id, { name: editName.trim() })
    setEditId(null)
    await refresh()
  }
  async function toggleActive(b: BranchRef) {
    await api.updateBranch(b.id, { active: b.active !== 1 })
    await refresh()
  }
  async function remove(b: BranchRef) {
    const msg =
      b.cases > 0
        ? `${b.name} has ${b.cases} case${b.cases === 1 ? '' : 's'}, so it will be archived (hidden but kept for history). Continue?`
        : `Remove ${b.name}?`
    if (!window.confirm(msg)) return
    await api.deleteBranch(b.id)
    await refresh()
  }

  return (
    <div className="card">
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Branches</div>
      <p className="page-sub" style={{ marginBottom: 12 }}>
        Locations within your company. Admins see all branches; team members can be limited to
        specific ones (set below, per person). Leave empty if you operate as a single location.
      </p>

      {loading ? (
        <div style={{ color: 'var(--gray)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          {branches.length === 0 && (
            <div style={{ color: 'var(--gray)', fontSize: 14 }}>No branches yet.</div>
          )}
          {branches.map((b) => (
            <div
              key={b.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid var(--line)', paddingTop: 8 }}
            >
              {editId === b.id ? (
                <>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                    autoFocus
                  />
                  <button className="link-btn" onClick={() => void saveName(b.id)}>Save</button>
                  <button className="link-btn" onClick={() => setEditId(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{b.name}</span>
                    {b.active !== 1 && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--gray)' }}>
                        ARCHIVED
                      </span>
                    )}
                    <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--gray)' }}>
                      {b.cases} case{b.cases === 1 ? '' : 's'}
                    </span>
                  </div>
                  <button
                    className="link-btn"
                    onClick={() => {
                      setEditId(b.id)
                      setEditName(b.name)
                    }}
                  >
                    Rename
                  </button>
                  <button className="link-btn" onClick={() => void toggleActive(b)}>
                    {b.active === 1 ? 'Archive' : 'Restore'}
                  </button>
                  <button className="link-btn" style={{ color: 'var(--red)' }} onClick={() => void remove(b)}>
                    Delete
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={add} style={{ display: 'flex', gap: 8 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New branch name (e.g. Knoxville)"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button className="btn btn-primary" type="submit" disabled={busy || !name.trim()}>
          {busy ? 'Adding…' : 'Add branch'}
        </button>
      </form>
    </div>
  )
}

function TeamSection() {
  const { user } = useAuth()
  const [team, setTeam] = useState<TeamUser[]>([])
  const [branches, setBranches] = useState<BranchRef[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [editBr, setEditBr] = useState<string | null>(null)
  const [draft, setDraft] = useState<string[]>([])

  async function refresh() {
    setLoading(true)
    try {
      const [u, b] = await Promise.all([api.listUsers(), api.listBranches()])
      setTeam(u.users)
      setBranches(b.branches)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void refresh()
  }, [])

  const hasBranches = branches.length > 0
  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? '—'
  async function saveBranches(u: TeamUser) {
    await api.updateUser(u.id, { branch_ids: draft })
    setEditBr(null)
    await refresh()
  }

  async function toggleNotify(u: TeamUser) {
    await api.updateUser(u.id, { notify_on_new_damage: !u.notify_on_new_damage })
    await refresh()
  }
  async function changeRole(u: TeamUser, role: Role) {
    await api.updateUser(u.id, { role })
    await refresh()
  }
  async function remove(u: TeamUser) {
    if (!window.confirm(`Remove ${u.name}?`)) return
    try {
      await api.deleteUser(u.id)
      await refresh()
    } catch {
      alert('Could not remove — a company must keep at least one admin.')
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, flex: 1 }}>Team members</div>
        <button className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add user'}
        </button>
      </div>

      {adding && (
        <AddUserForm
          onCreated={() => {
            setAdding(false)
            void refresh()
          }}
        />
      )}

      {loading ? (
        <div style={{ color: 'var(--gray)' }}>Loading…</div>
      ) : (
        <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              {hasBranches && <th>Branch access</th>}
              <th>Email alerts</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {team.map((u) => (
              <Fragment key={u.id}>
              <tr>
                <td style={{ fontWeight: 600 }}>
                  {u.name}
                  {u.id === user?.id && <span style={{ color: 'var(--gray)', fontWeight: 400 }}> (you)</span>}
                </td>
                <td><EmailLink email={u.email} /></td>
                <td>
                  <select
                    value={u.role}
                    onChange={(e) => void changeRole(u, e.target.value as Role)}
                    style={selectSmall}
                  >
                    <option value="company_admin">Admin</option>
                    <option value="company_user">User</option>
                  </select>
                </td>
                {hasBranches && (
                  <td style={{ fontSize: 13 }}>
                    {u.role === 'company_admin' ? (
                      <span style={{ color: 'var(--gray)' }}>All branches</span>
                    ) : (
                      <>
                        {u.branch_ids.length === 0 ? (
                          <span style={{ color: 'var(--gray)' }}>All branches</span>
                        ) : (
                          u.branch_ids.map(branchName).join(', ')
                        )}{' '}
                        <button
                          className="link-btn"
                          onClick={() => {
                            setEditBr(editBr === u.id ? null : u.id)
                            setDraft(u.branch_ids)
                          }}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </td>
                )}
                <td>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(u.notify_on_new_damage)}
                      onChange={() => void toggleNotify(u)}
                    />
                    <span style={{ fontSize: 13 }}>{u.notify_on_new_damage ? 'On' : 'Off'}</span>
                  </label>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {u.id !== user?.id && (
                    <button className="link-btn" style={{ color: 'var(--red)' }} onClick={() => void remove(u)}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
              {hasBranches && editBr === u.id && (
                <tr>
                  <td colSpan={6} style={{ background: 'var(--bg)' }}>
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 6 }}>
                        Limit {u.name} to these branches (none checked = all branches):
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                        {branches
                          .filter((b) => b.active === 1 || draft.includes(b.id))
                          .map((b) => (
                            <label key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={draft.includes(b.id)}
                                onChange={(e) =>
                                  setDraft((d) =>
                                    e.target.checked ? [...d, b.id] : d.filter((x) => x !== b.id),
                                  )
                                }
                              />
                              <span style={{ fontSize: 13 }}>{b.name}</span>
                            </label>
                          ))}
                      </div>
                      <button className="btn btn-primary" onClick={() => void saveBranches(u)}>Save</button>
                      <button className="link-btn" style={{ marginLeft: 12 }} onClick={() => setEditBr(null)}>Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  )
}

function TechnicianSection({ formUrlBase }: { formUrlBase: string }) {
  const [techs, setTechs] = useState<TechnicianRef[]>([])
  const [sups, setSups] = useState<SupervisorRef[]>([])
  const [branches, setBranches] = useState<BranchRef[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)

  async function refresh() {
    setLoading(true)
    try {
      const [t, s, b] = await Promise.all([
        api.listTechnicians(),
        api.listSupervisors(),
        api.listBranches(),
      ])
      setTechs(t.technicians)
      setSups(s.supervisors)
      setBranches(b.branches)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void refresh()
  }, [])

  const activeBranches = branches.filter((b) => b.active === 1)
  const hasBranches = branches.length > 0

  async function assignSupervisor(t: TechnicianRef, supervisorId: string) {
    await api.updateTechnician(t.id, { supervisor_id: supervisorId || null })
    await refresh()
  }
  async function assignBranch(t: TechnicianRef, branchId: string) {
    await api.updateTechnician(t.id, { branch_id: branchId || null })
    await refresh()
  }

  async function remove(t: TechnicianRef) {
    if (!window.confirm(`Remove ${t.name}? Their past cases keep their name.`)) return
    await api.deleteTechnician(t.id)
    await refresh()
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ fontWeight: 700, flex: 1 }}>Technicians</div>
        <button className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add technician'}
        </button>
      </div>
      <p className="page-sub" style={{ marginBottom: 12 }}>
        Field techs pick their name on the shared form, or use their own personal link.
      </p>

      {adding && (
        <AddTechForm
          supervisors={sups}
          branches={activeBranches}
          onCreated={() => {
            setAdding(false)
            void refresh()
          }}
        />
      )}

      {loading ? (
        <div style={{ color: 'var(--gray)' }}>Loading…</div>
      ) : techs.length === 0 ? (
        <p style={{ color: 'var(--gray)', fontSize: 14 }}>No technicians yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Supervisor</th>
                {hasBranches && <th>Branch</th>}
                <th>Personal link</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {techs.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>
                    <Link to={`/app/technicians/${t.id}`}>{t.name}</Link>
                  </td>
                  <td>{t.phone ?? '—'}</td>
                  <td>
                    <select
                      value={t.supervisor_id ?? ''}
                      onChange={(e) => void assignSupervisor(t, e.target.value)}
                      style={selectSmall}
                      disabled={sups.length === 0}
                    >
                      <option value="">— None —</option>
                      {sups.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </td>
                  {hasBranches && (
                    <td>
                      <select
                        value={t.branch_id ?? ''}
                        onChange={(e) => void assignBranch(t, e.target.value)}
                        style={selectSmall}
                      >
                        <option value="">— None —</option>
                        {activeBranches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td>
                    <CopyButton url={`${formUrlBase}/t/${t.id}`} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="link-btn" style={{ color: 'var(--red)' }} onClick={() => void remove(t)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AddTechForm({
  supervisors,
  branches,
  onCreated,
}: {
  supervisors: SupervisorRef[]
  branches: BranchRef[]
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [supervisorId, setSupervisorId] = useState('')
  const [branchId, setBranchId] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.createTechnician({
        name,
        phone,
        email,
        supervisor_id: supervisorId || null,
        branch_id: branchId || null,
      })
      onCreated()
    } finally {
      setBusy(false)
    }
  }
  return (
    <form onSubmit={onSubmit} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Phone (optional)</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '10px 0' }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Email (optional)</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Supervisor</label>
          <select
            value={supervisorId}
            onChange={(e) => setSupervisorId(e.target.value)}
            style={selectSmall}
            disabled={supervisors.length === 0}
          >
            <option value="">— None —</option>
            {supervisors.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>
      {branches.length > 0 && (
        <div className="field" style={{ margin: '0 0 10px', maxWidth: 260 }}>
          <label>Branch (optional)</label>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={selectSmall}>
            <option value="">— None —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}
      <button className="btn btn-primary" type="submit" disabled={busy}>
        {busy ? 'Adding…' : 'Add technician'}
      </button>
    </form>
  )
}

function SupervisorSection() {
  const [sups, setSups] = useState<SupervisorRef[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)

  async function refresh() {
    setLoading(true)
    try {
      const r = await api.listSupervisors()
      setSups(r.supervisors)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void refresh()
  }, [])

  async function remove(s: SupervisorRef) {
    if (!window.confirm(`Remove ${s.name}? Technicians assigned to them will have no supervisor.`)) return
    await api.deleteSupervisor(s.id)
    await refresh()
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ fontWeight: 700, flex: 1 }}>Supervisors</div>
        <button className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add supervisor'}
        </button>
      </div>
      <p className="page-sub" style={{ marginBottom: 12 }}>
        Assign a supervisor to each technician and their name and number fill in on the form
        automatically.
      </p>

      {adding && (
        <AddSupForm
          onCreated={() => {
            setAdding(false)
            void refresh()
          }}
        />
      )}

      {loading ? (
        <div style={{ color: 'var(--gray)' }}>Loading…</div>
      ) : sups.length === 0 ? (
        <p style={{ color: 'var(--gray)', fontSize: 14 }}>No supervisors yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sups.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td>{s.phone ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="link-btn" style={{ color: 'var(--red)' }} onClick={() => void remove(s)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AddSupForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.createSupervisor({ name, phone, email })
      onCreated()
    } finally {
      setBusy(false)
    }
  }
  return (
    <form onSubmit={onSubmit} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Phone</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </div>
      <div className="field" style={{ margin: '10px 0' }}>
        <label>Email (optional)</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy}>
        {busy ? 'Adding…' : 'Add supervisor'}
      </button>
    </form>
  )
}

function CopyButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      className="link-btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          /* clipboard blocked */
        }
      }}
    >
      {copied ? 'Copied!' : 'Copy link'}
    </button>
  )
}

function AddUserForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('company_user')
  const [notify, setNotify] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.createUser({ name, email, password, role, notify_on_new_damage: notify })
      onCreated()
    } catch {
      setError('Could not add user (email may already be in use, or password under 8 characters).')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Temporary password</label>
          <input value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} style={selectSmall}>
            <option value="company_user">User</option>
            <option value="company_admin">Admin</option>
          </select>
        </div>
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '12px 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        <span style={{ fontSize: 14 }}>Email this user when a new damage is reported</span>
      </label>
      {error && <p className="form-error">{error}</p>}
      <div>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add user'}
        </button>
      </div>
    </form>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}
const selectSmall: React.CSSProperties = { ...inputStyle, padding: '6px 8px' }
